import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {Field} from '@app/interfaces';
import {Observable, Subject} from 'rxjs';
import {BusinessUnitsService} from "@app/services/accounts/business-units.service";
import {map, shareReplay, take, takeUntil} from "rxjs/operators";
import {BusinessUnit} from "@app/interfaces/entities/accounts/BusinessUnit";

@Component({
    selector: 'app-form-input-business-unit',
    templateUrl: './form-input-business-unit.component.html',
    styleUrls: ['./form-input-business-unit.component.scss'],
    standalone: false
})
export class FormInputBusinessUnitComponent implements OnInit, OnDestroy {

    @Input() control: AbstractControl;

    /**
     * Seed the picker with the branch selected in the navbar when the entity has
     * none of its own. Only for deliberate "assign a branch" UI (the assign
     * dialog) — a form rendering an existing record must show what that record
     * actually has. It used to seed unconditionally, so the vehicle form
     * displayed "Poslovalnica: Enota 1" for vehicles with no branch at all,
     * while the advertising tab correctly asked for one (BRIEFD-7094).
     */
    @Input() defaultToSelectedUnit = false;

    field: Field<number>;

    public dropdownOptions$: Observable<BusinessUnit[]>;

    /**
     * Why the other branches are greyed out. The picker deliberately only lets
     * you assign the branch selected in the navbar (BRIEFD-7336), which read as
     * "the dropdown is broken" — this says which of the two reasons applies.
     */
    public lockReason$: Observable<'single' | 'navbar' | null>;
    private destroy$ = new Subject<void>();

    constructor(private businessUnitsService: BusinessUnitsService) {
    }

    ngOnInit(): void {
        this.control.disable();

        const units$ = this.businessUnitsService.getSelectedBusinessUnit().pipe(takeUntil(this.destroy$), shareReplay(1));

        this.dropdownOptions$ = units$.pipe(
            map(([selected, allUnits]) => {
                // Limit options to only the selected business unit and active units
                return allUnits.map(unit => ({...unit, isActive: unit.isActive && (!selected?.uuid || unit.uuid === selected?.uuid)}))
            })
        );

        this.lockReason$ = units$.pipe(
            map(([selected, allUnits]) => {
                // Only the units this user may access are returned at all
                // (AccountConfigResource, BRIEFD-6724), so a single one means
                // there is nothing to switch to in the first place.
                if ((allUnits ?? []).filter((unit) => unit.isActive).length <= 1) {
                    return 'single';
                }
                return selected?.uuid ? 'navbar' : null;
            }),
        );

        this.dropdownOptions$.subscribe((options) => {
            this.control.enable();
        });

        units$.subscribe((([selectedUnit]) => {
            // The parent form host (vehicle-form-page) re-creates this
            // component every time the user switches tabs, because the
            // template uses `@if (layout === selectedSectionItem)` to mount
            // only the active section. The FormGroup itself persists across
            // tab switches, so `this.control.value` already holds whatever
            // the user picked last. Don't clobber it — only seed an initial
            // value when the control is genuinely empty.
            if (this.control.value) {
                return;
            }
            if (this.field.value !== null && this.field.value !== undefined) {
                this.control.setValue(this.field.value);
            } else if (this.defaultToSelectedUnit && selectedUnit?.uuid) {
                this.control.setValue(selectedUnit.uuid);
            }
        }))
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    @Input('field')
    set setField(field: Field<number>) {
        this.field = field;

        if (field.value && this.control?.value) {
            this.dropdownOptions$.pipe(take(1)).subscribe((options) => {
                const selected = options.find((option) => option.uuid === this.control.value);

                if (selected) {
                    this.control.setValue(selected.uuid);
                }
            });
        }
    }
}
